import { useRef } from 'react';
import { useFrame } from '@react-three/fiber';
import * as THREE from 'three';
import { scrollProgress, transitionProgress } from '../../lib/atlas';

// Approach: deep space → close to the surface
const approachCurve = new THREE.QuadraticBezierCurve3(
  new THREE.Vector3(0, 0.35, 11.2),
  new THREE.Vector3(1.9, 0.15, 7.0),
  new THREE.Vector3(0.4, 0.12, 2.8)
);

// Pull-back: surface → receding orbit view (the Earth-to-map handoff)
const pullBackCurve = new THREE.QuadraticBezierCurve3(
  new THREE.Vector3(0.4, 0.12, 2.8),
  new THREE.Vector3(0.9, 0.55, 6.6),
  new THREE.Vector3(-1.5, 0.75, 10.4)
);

const lookStart = new THREE.Vector3(-1.55, -0.25, 0);
const lookEnd = new THREE.Vector3(0, 0.05, 0);

const tmpPos = new THREE.Vector3();
const tmpLook = new THREE.Vector3();

export default function CameraRig({ reducedMotion }) {
  const currentA = useRef(0);
  const currentB = useRef(0);

  useFrame((state, delta) => {
    const targetA = reducedMotion ? 0.82 : scrollProgress.value;
    const targetB = reducedMotion ? 0 : transitionProgress.value;
    currentA.current = THREE.MathUtils.damp(currentA.current, targetA, 3.2, delta);
    currentB.current = THREE.MathUtils.damp(currentB.current, targetB, 2.6, delta);

    const ta = currentA.current;
    const tb = currentB.current;

    if (tb <= 0.01) {
      // Approach phase (unchanged opening sequence)
      const p = ta * ta * (3 - 2 * ta);
      approachCurve.getPoint(p, tmpPos);
      if (!reducedMotion) {
        const parallax = 1 - p * 0.6;
        tmpPos.x += state.pointer.x * 0.18 * parallax;
        tmpPos.y += state.pointer.y * 0.12 * parallax;
      }
      state.camera.position.copy(tmpPos);
      tmpLook.lerpVectors(lookStart, lookEnd, p);
      state.camera.lookAt(tmpLook);
      const fov = 50 + p * 10;
      if (Math.abs(state.camera.fov - fov) > 0.01) {
        state.camera.fov = fov;
        state.camera.updateProjectionMatrix();
      }
      return;
    }

    // Pull-back phase: the planet recedes, framing the handoff to the flat map
    const q = tb * tb * (3 - 2 * tb);
    pullBackCurve.getPoint(q, tmpPos);
    if (!reducedMotion) {
      const parallax = 0.4 * (1 - q);
      tmpPos.x += state.pointer.x * 0.18 * parallax;
      tmpPos.y += state.pointer.y * 0.12 * parallax;
    }
    state.camera.position.copy(tmpPos);
    tmpLook.set(0, 0.05, 0).lerp(new THREE.Vector3(0, 0, 0), q);
    state.camera.lookAt(tmpLook);
    const fovB = 60 - q * 12;
    if (Math.abs(state.camera.fov - fovB) > 0.01) {
      state.camera.fov = fovB;
      state.camera.updateProjectionMatrix();
    }
  });

  return null;
}
